import { bridge } from "@dinahmoe/dmafjs-core";
import { assetDictionary } from "@dinahmoe/dmafjs-assetloader";

import { visualEntities } from "../entities.js";
import { create } from "../helpers/elements.helper.js";
import { VisualSuperHtml } from "./visual-super.environment.js";

class ImageHtml extends VisualSuperHtml {
  handlers = this.extendParentHandlers({
    "source.imageId": this.setImageId,
    "source.url": this.setUrl,
    "source.crossOrigin": this.setCrossOrigin,
    "source.objectFit": this.setObjectFit,
    "source.objectPosition": this.setObjectPosition,
    "source.allowSave": this.setAllowSave,
    "source.alt": this.setAlt,
  });

  setProps(eNode) {
    if (eNode.props["source.objectFit"] === "repeat") {
      this._setPropsRepeat(eNode);
      return;
    }

    const imgElement = document.createElement("img");
    imgElement.setAttribute("draggable", "false");
    imgElement.addEventListener("dragstart", (event) => event.preventDefault());

    const imageId = eNode.props["source.imageId"];
    const url = eNode.props["source.url"];

    if (imageId) {
      const assetElement = assetDictionary.get(`assets.image.${imageId}`);
      if (assetElement) {
        imgElement.setAttribute("src", assetElement.src);
        assetDictionary.set(`assets.image.${imageId}`, assetElement.cloneNode());
      }
    }

    eNode.element = create(eNode, imgElement);

    if (url && !imageId) {
      this.setCrossOrigin(eNode);
      imgElement.setAttribute("src", url);
    }

    if (eNode.props["source.objectFit"]) {
      eNode.element.style.objectFit = eNode.props["source.objectFit"];
    }
    if (eNode.props["source.objectPosition"]) {
      eNode.element.style.objectPosition = eNode.props["source.objectPosition"];
    }
    eNode.element.style.overflow = "hidden";

    if (eNode.props["source.allowSave"]) {
      eNode.element.style.pointerEvents = "auto";
    }

    super.setProps(eNode);

    imgElement.addEventListener("load", () => {
      bridge.update({
        uid: eNode.uid,
        key: "image_loaded",
        value: {
          id: eNode.props["id"],
          naturalWidth: imgElement.naturalWidth,
          naturalHeight: imgElement.naturalHeight,
        },
        sender: "browser",
      });
    });

    imgElement.addEventListener("error", () =>
      bridge.update({
        uid: eNode.uid,
        key: "image_load_error",
        value: {
          id: eNode.props["id"],
        },
        sender: "browser",
      }),
    );
  }

  _setPropsRepeat(eNode) {
    eNode.element = create(eNode, "div");
    eNode.element.style.backgroundRepeat = "repeat";

    const imageId = eNode.props["source.imageId"];
    if (imageId) {
      const imageData = assetDictionary.get(`assets.image.${imageId}`);
      if (imageData) {
        eNode.element.style.backgroundImage = `url("${imageData.src}")`;
        eNode.element.style.backgroundSize = `${imageData.width}px ${imageData.height}px`;
      }
    }

    super.setProps(eNode);
  }

  setImageId(eNode) {
    const imageId = eNode.props["source.imageId"];
    if (!imageId) {
      eNode.element.style.visibility = "hidden";
      return;
    }

    if (eNode.props["source.objectFit"] === "repeat") {
      const imageData = assetDictionary.get(`assets.image.${imageId}`);
      if (imageData) {
        eNode.element.style.backgroundImage = `url("${imageData.src}")`;
        eNode.element.style.backgroundSize = `${imageData.width}px ${imageData.height}px`;
      }
      return;
    }

    const assetElement = assetDictionary.get(`assets.image.${imageId}`);
    if (!assetElement) {
      console.warn(`Missing image: ${imageId}`);
      return;
    }

    eNode.element.setAttribute("src", assetElement.src);
  }

  setUrl(eNode) {
    if (eNode.element) {
      this.setCrossOrigin(eNode);
      eNode.element.setAttribute("src", eNode.props["source.url"]);
    }
  }

  setCrossOrigin(eNode) {
    const value = eNode.props["source.crossOrigin"];
    if (value && value !== "none") {
      eNode.element.setAttribute("crossorigin", value);
    } else {
      eNode.element.removeAttribute("crossorigin");
    }
  }

  setObjectFit(eNode) {
    if (eNode.element) {
      eNode.element.style.objectFit = eNode.props["source.objectFit"];
    }
  }

  setObjectPosition(eNode) {
    if (eNode.element) {
      eNode.element.style.objectPosition = eNode.props["source.objectPosition"];
    }
  }

  setAllowSave(eNode) {
    if (eNode.props["source.allowSave"]) {
      eNode.element.style.pointerEvents = "auto";
    }
  }

  setAlt(eNode) {
    eNode.element.setAttribute("alt", eNode.props["source.alt"] || "");
  }
}

const imageHtml = new ImageHtml("visual:image");
visualEntities.set("visual:image", imageHtml);
export { ImageHtml, imageHtml };
